import { View, StyleSheet, ViewStyle, StyleProp, ScrollView } from 'react-native';
import { theme } from '@socrait/react';

import { Loading } from '~/components/Loading';
import { ColumnConfig } from './types';
import { TableHeader } from './TableHeader';
import { TableBody } from './TableBody';

export interface TableProps<T> {
  data: T[];
  columns: ColumnConfig<T>[];
  isLoading?: boolean;
  columnGap?: number;
  style?: StyleProp<ViewStyle>;
  headerStyle?: StyleProp<ViewStyle>;
  rowStyle?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
  rowHeight?: number;
  keyExtractor?: (item: T, index: number) => string;
  minWidth?: number;
  maxWidth?: number;
}

export function Table<T>({
  data,
  columns,
  isLoading = false,
  columnGap = 20,
  style,
  headerStyle,
  rowStyle,
  accessibilityLabel,
  rowHeight,
  keyExtractor,
  minWidth,
  maxWidth,
}: TableProps<T>) {
  const tableContent = (
    <View style={[styles.tableContent, { minWidth, maxWidth }]}>
      <TableHeader columns={columns} columnGap={columnGap} style={headerStyle} />
      <TableBody
        data={data}
        columns={columns}
        columnGap={columnGap}
        rowStyle={rowStyle}
        rowHeight={rowHeight}
        keyExtractor={keyExtractor}
      />
    </View>
  );

  return (
    <View accessibilityLabel={accessibilityLabel} style={[styles.tableContainer, style]}>
      <ScrollView style={styles.verticalScrollView} showsVerticalScrollIndicator={true}>
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={true}
          style={styles.horizontalScrollView}
          contentContainerStyle={{ width: '100%' }}>
          {tableContent}
        </ScrollView>
      </ScrollView>
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <Loading backgroundColor={theme.colors.white} />
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
  },
  verticalScrollView: {
    flex: 1,
  },
  horizontalScrollView: {
    flexGrow: 0,
  },
  tableContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.medium,
    width: '100%',
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
  },
});
